import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type AuthConfig } from '../api';

const ERRORS: Record<string, string> = {
  domain_not_allowed: 'Sign-in is restricted to surfboard.se Google accounts.',
  no_email: 'Your Google account did not return an email address.',
  auth_failed: 'Sign-in failed. Please try again.',
};

export function LoginPage() {
  const [params] = useSearchParams();
  const [config, setConfig] = useState<AuthConfig | null>(null);

  useEffect(() => {
    api
      .authConfig()
      .then(setConfig)
      .catch(() => setConfig({ googleConfigured: false, allowedDomain: 'surfboard.se' }));
  }, []);

  const error = params.get('error');
  const next = params.get('next') ?? '/';
  const startUrl = `/api/auth/google?next=${encodeURIComponent(next)}`;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="relative min-h-screen overflow-hidden bg-ink paper-grain text-cream">
      {/* ─── Top ticker ─── */}
      <div className="overflow-hidden border-b hair-strong">
        <div className="marquee py-1.5 text-[10px] tracking-widestest text-cream-500">
          {[0, 1].map((k) => (
            <div key={k} className="flex shrink-0 items-center gap-8 pr-8 font-mono">
              {['SURFBOARD PORTAL', `ISSUE · ${today}`, 'CREDENTIALED ENTRY', 'SET IN FRAUNCES', 'INTERNAL ONLY'].map(
                (t, i) => (
                  <span key={i} className="flex items-center gap-3">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-coral animate-pulse-dot" />
                    {t}
                  </span>
                ),
              )}
            </div>
          ))}
        </div>
      </div>

      <main className="relative mx-auto grid min-h-[calc(100vh-32px)] max-w-7xl grid-cols-12 items-stretch gap-0 px-6 md:px-10">
        {/* ─── LEFT: editorial brand block ─── */}
        <section className="relative col-span-12 flex flex-col justify-between py-16 md:col-span-7 md:py-24">
          <DecorativeArc />

          <div className="relative animate-rise">
            <p className="ix mb-6 flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-coral" />
              Portal · Sign-in
            </p>

            <h1 className="font-display text-[15vw] font-light leading-[0.84] tracking-tightest text-cream md:text-[10.5rem]">
              Surf<span
                className="italic text-coral"
                style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'WONK' 1" }}
              >
                board.
              </span>
            </h1>

            <p className="mt-8 max-w-xl text-pretty font-display text-2xl leading-snug text-cream-700 md:text-3xl">
              All your systems, gathered in one volume.{' '}
              <span className="text-cream">Sign in with your company Google account to start reading.</span>
            </p>
          </div>

          {/* Footer rule */}
          <div className="relative mt-12 flex flex-wrap items-center justify-between gap-3 border-t hair-strong pt-5 font-mono text-[10px] uppercase tracking-widestest text-cream-500">
            <span>portal.surfboard.cloud</span>
            <span className="hidden md:inline">Distribution · Internal</span>
            <span>{today}</span>
          </div>
        </section>

        {/* ─── RIGHT: sign-in card ─── */}
        <aside className="relative col-span-12 flex items-center border-t hair-strong md:col-span-5 md:border-l md:border-t-0">
          <div
            className="relative w-full animate-rise px-0 py-10 md:px-12 md:py-24"
            style={{ animationDelay: '120ms' }}
          >
            <p className="ix mb-4">№ 01 · Enter</p>
            <h2 className="font-display text-5xl font-medium leading-tight tracking-tightish text-cream md:text-6xl">
              Sign in.
            </h2>
            <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-cream-700">
              Use your{' '}
              <span className="font-mono text-cream">
                @{config?.allowedDomain ?? 'surfboard.se'}
              </span>{' '}
              Google account. Other domains are politely turned away at the door.
            </p>

            {error && (
              <div className="mt-7 border-l-2 border-coral bg-coral/10 px-4 py-3 font-mono text-[12px] uppercase tracking-wider text-coral-300">
                {ERRORS[error] ?? error}
              </div>
            )}

            {config && !config.googleConfigured ? (
              <div className="mt-7 border-l-2 border-amber-400 bg-amber-400/10 px-4 py-3 text-[12px] leading-relaxed text-amber-200">
                <p className="font-mono uppercase tracking-wider">Setup required</p>
                <p className="mt-2 text-cream-700">
                  Google OAuth isn't configured. Set{' '}
                  <code className="rounded-sm bg-ink-700 px-1 font-mono text-amber-200">
                    GOOGLE_CLIENT_ID
                  </code>{' '}
                  and{' '}
                  <code className="rounded-sm bg-ink-700 px-1 font-mono text-amber-200">
                    GOOGLE_CLIENT_SECRET
                  </code>
                  , then restart the portal.
                </p>
              </div>
            ) : (
              <a
                href={startUrl}
                className="group mt-8 inline-flex w-full items-center justify-between gap-3 rounded-sm bg-coral px-5 py-4 text-left text-[15px] font-medium text-ink transition-all duration-300 hover:bg-coral-400 hover:translate-x-1"
              >
                <span className="flex items-center gap-3">
                  <GoogleLogo />
                  Continue with Google
                </span>
                <span aria-hidden className="text-xl transition-transform group-hover:translate-x-1">
                  →
                </span>
              </a>
            )}

            <div className="mt-10 border-t hair pt-5 font-mono text-[10px] uppercase tracking-widestest text-cream-500">
              By signing in you accept that we'll remember it's you.
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}

function DecorativeArc() {
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute -left-32 top-1/3 h-[700px] w-[120%] opacity-[0.08]"
      viewBox="0 0 1400 700"
      fill="none"
    >
      <path
        d="M-50 480 C 250 280, 500 640, 800 380 S 1300 140, 1500 340"
        stroke="#F4ECDD"
        strokeWidth="1.5"
      />
      <path
        d="M-50 540 C 250 340, 500 700, 800 440 S 1300 200, 1500 400"
        stroke="#FF5A3C"
        strokeWidth="1"
      />
      <path
        d="M-50 600 C 250 400, 500 760, 800 500 S 1300 260, 1500 460"
        stroke="#F4ECDD"
        strokeWidth="1"
        strokeDasharray="4 8"
      />
    </svg>
  );
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.79 2.72v2.26h2.9c1.7-1.57 2.69-3.88 2.69-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.46-.8 5.95-2.18l-2.9-2.26c-.8.54-1.83.87-3.05.87-2.35 0-4.34-1.59-5.05-3.72H.96v2.34A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.95 10.71A5.41 5.41 0 0 1 3.66 9c0-.59.1-1.17.29-1.71V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.05l2.99-2.34z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.46 3.43 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l2.99 2.34A5.39 5.39 0 0 1 9 3.58z"
      />
    </svg>
  );
}
